'use client';

import { SVGProps } from 'react';

// Import only the icons that are actually used in the application
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowUpTrayIcon,
  Bars3Icon,
  BoltIcon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  DevicePhoneMobileIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  FireIcon,
  GiftIcon,
  GlobeAltIcon,
  HashtagIcon,
  HeartIcon,
  HomeIcon,
  InboxIcon,
  InformationCircleIcon,
  LightBulbIcon,
  LinkIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PaintBrushIcon,
  PaperAirplaneIcon,
  PauseIcon,
  PencilIcon,
  PhotoIcon,
  PlayIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
  TrashIcon,
  TrophyIcon,
  TvIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import {
  ArrowDownIcon as ArrowDownIconSolid,
  ArrowLeftIcon as ArrowLeftIconSolid,
  ArrowPathIcon as ArrowPathIconSolid,
  ArrowRightIcon as ArrowRightIconSolid,
  ArrowRightOnRectangleIcon as ArrowRightOnRectangleIconSolid,
  ArrowsPointingInIcon as ArrowsPointingInIconSolid,
  ArrowsPointingOutIcon as ArrowsPointingOutIconSolid,
  ArrowTopRightOnSquareIcon as ArrowTopRightOnSquareIconSolid,
  ArrowUpIcon as ArrowUpIconSolid,
  ArrowUpTrayIcon as ArrowUpTrayIconSolid,
  Bars3Icon as Bars3IconSolid,
  BoltIcon as BoltIconSolid,
  CalendarIcon as CalendarIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  CheckBadgeIcon as CheckBadgeIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  CheckIcon as CheckIconSolid,
  ChevronDownIcon as ChevronDownIconSolid,
  ChevronLeftIcon as ChevronLeftIconSolid,
  ChevronRightIcon as ChevronRightIconSolid,
  ChevronUpIcon as ChevronUpIconSolid,
  ClipboardDocumentIcon as ClipboardDocumentIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  ClockIcon as ClockIconSolid,
  CodeBracketIcon as CodeBracketIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  DevicePhoneMobileIcon as DevicePhoneMobileIconSolid,
  EllipsisVerticalIcon as EllipsisVerticalIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
  FireIcon as FireIconSolid,
  ExclamationCircleIcon as ExclamationCircleIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  EyeIcon as EyeIconSolid,
  EyeSlashIcon as EyeSlashIconSolid,
  GiftIcon as GiftIconSolid,
  GlobeAltIcon as GlobeAltIconSolid,
  HashtagIcon as HashtagIconSolid,
  HeartIcon as HeartIconSolid,
  HomeIcon as HomeIconSolid,
  InboxIcon as InboxIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
  LightBulbIcon as LightBulbIconSolid,
  LinkIcon as LinkIconSolid,
  ListBulletIcon as ListBulletIconSolid,
  LockClosedIcon as LockClosedIconSolid,
  MagnifyingGlassIcon as MagnifyingGlassIconSolid,
  PaintBrushIcon as PaintBrushIconSolid,
  PaperAirplaneIcon as PaperAirplaneIconSolid,
  PauseIcon as PauseIconSolid,
  PencilIcon as PencilIconSolid,
  PhotoIcon as PhotoIconSolid,
  PlayIcon as PlayIconSolid,
  PlusIcon as PlusIconSolid,
  QuestionMarkCircleIcon as QuestionMarkCircleIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  SparklesIcon as SparklesIconSolid,
  StarIcon as StarIconSolid,
  TrashIcon as TrashIconSolid,
  TrophyIcon as TrophyIconSolid,
  TvIcon as TvIconSolid,
  UserCircleIcon as UserCircleIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  UserIcon as UserIconSolid,
  UserPlusIcon as UserPlusIconSolid,
  UsersIcon as UsersIconSolid,
  XCircleIcon as XCircleIconSolid,
  XMarkIcon as XMarkIconSolid,
  ServerStackIcon as ServerStackIconSolid,
} from '@heroicons/react/24/solid';

type SVGComponentProps = Omit<SVGProps<SVGSVGElement>, 'ref'>;
type IconComponent = React.ComponentType<SVGComponentProps>;

// Map of icon names to their outline components
const outlineIcons: Record<string, IconComponent> = {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowUpTrayIcon,
  Bars3Icon,
  BoltIcon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  DevicePhoneMobileIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  FireIcon,
  GiftIcon,
  GlobeAltIcon,
  HashtagIcon,
  HeartIcon,
  HomeIcon,
  InboxIcon,
  InformationCircleIcon,
  LightBulbIcon,
  LinkIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PaintBrushIcon,
  PaperAirplaneIcon,
  PauseIcon,
  PencilIcon,
  PhotoIcon,
  PlayIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
  TrashIcon,
  TrophyIcon,
  TvIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
  XCircleIcon,
  XMarkIcon,
};

// Map of icon names to their solid components
const solidIcons: Record<string, IconComponent> = {
  ArrowDownIcon: ArrowDownIconSolid,
  ArrowLeftIcon: ArrowLeftIconSolid,
  ArrowPathIcon: ArrowPathIconSolid,
  ArrowRightIcon: ArrowRightIconSolid,
  ArrowRightOnRectangleIcon: ArrowRightOnRectangleIconSolid,
  ArrowsPointingInIcon: ArrowsPointingInIconSolid,
  ArrowsPointingOutIcon: ArrowsPointingOutIconSolid,
  ArrowTopRightOnSquareIcon: ArrowTopRightOnSquareIconSolid,
  ArrowUpIcon: ArrowUpIconSolid,
  ArrowUpTrayIcon: ArrowUpTrayIconSolid,
  Bars3Icon: Bars3IconSolid,
  BoltIcon: BoltIconSolid,
  CalendarIcon: CalendarIconSolid,
  ChartBarIcon: ChartBarIconSolid,
  ChatBubbleLeftRightIcon: ChatBubbleLeftRightIconSolid,
  CheckBadgeIcon: CheckBadgeIconSolid,
  CheckCircleIcon: CheckCircleIconSolid,
  CheckIcon: CheckIconSolid,
  ChevronDownIcon: ChevronDownIconSolid,
  ChevronLeftIcon: ChevronLeftIconSolid,
  ChevronRightIcon: ChevronRightIconSolid,
  ChevronUpIcon: ChevronUpIconSolid,
  ClipboardDocumentIcon: ClipboardDocumentIconSolid,
  ClipboardDocumentListIcon: ClipboardDocumentListIconSolid,
  ClockIcon: ClockIconSolid,
  CodeBracketIcon: CodeBracketIconSolid,
  Cog6ToothIcon: Cog6ToothIconSolid,
  DevicePhoneMobileIcon: DevicePhoneMobileIconSolid,
  EllipsisVerticalIcon: EllipsisVerticalIconSolid,
  EnvelopeIcon: EnvelopeIconSolid,
  FireIcon: FireIconSolid,
  ExclamationCircleIcon: ExclamationCircleIconSolid,
  ExclamationTriangleIcon: ExclamationTriangleIconSolid,
  EyeIcon: EyeIconSolid,
  EyeSlashIcon: EyeSlashIconSolid,
  GiftIcon: GiftIconSolid,
  GlobeAltIcon: GlobeAltIconSolid,
  HashtagIcon: HashtagIconSolid,
  HeartIcon: HeartIconSolid,
  HomeIcon: HomeIconSolid,
  InboxIcon: InboxIconSolid,
  InformationCircleIcon: InformationCircleIconSolid,
  LightBulbIcon: LightBulbIconSolid,
  LinkIcon: LinkIconSolid,
  ListBulletIcon: ListBulletIconSolid,
  LockClosedIcon: LockClosedIconSolid,
  MagnifyingGlassIcon: MagnifyingGlassIconSolid,
  PaintBrushIcon: PaintBrushIconSolid,
  PaperAirplaneIcon: PaperAirplaneIconSolid,
  PauseIcon: PauseIconSolid,
  PencilIcon: PencilIconSolid,
  PhotoIcon: PhotoIconSolid,
  PlayIcon: PlayIconSolid,
  PlusIcon: PlusIconSolid,
  QuestionMarkCircleIcon: QuestionMarkCircleIconSolid,
  ShieldCheckIcon: ShieldCheckIconSolid,
  SparklesIcon: SparklesIconSolid,
  StarIcon: StarIconSolid,
  TrashIcon: TrashIconSolid,
  TrophyIcon: TrophyIconSolid,
  TvIcon: TvIconSolid,
  UserCircleIcon: UserCircleIconSolid,
  UserGroupIcon: UserGroupIconSolid,
  UserIcon: UserIconSolid,
  UserPlusIcon: UserPlusIconSolid,
  UsersIcon: UsersIconSolid,
  XCircleIcon: XCircleIconSolid,
  XMarkIcon: XMarkIconSolid,
  ServerStackIcon: ServerStackIconSolid,
};

type IconVariant = 'outline' | 'solid';

interface IconProps extends Omit<SVGComponentProps, 'onClick'> {
  name: string;
  variant?: IconVariant;
  size?: number;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

function Icon({
  name,
  variant = 'outline',
  size = 24,
  className = '',
  onClick,
  disabled = false,
  ...props
}: IconProps) {
  const iconSet = variant === 'solid' ? solidIcons : outlineIcons;
  const IconComponent = iconSet[name];

  if (!IconComponent) {
    // Fallback to QuestionMarkCircleIcon for unknown icons
    return (
      <QuestionMarkCircleIcon data-component-id="src/components/ui/AppIcon.tsx:325:6" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="325" data-component-end-line="333" data-component-file="AppIcon.tsx" data-component-name="QuestionMarkCircleIcon" data-component-content="%7B%22elementName%22%3A%22QuestionMarkCircleIcon%22%7D"
        width={size}
        height={size}
        className={`text-gray-400 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        onClick={disabled ? undefined : onClick}
        style={props.style}
        aria-hidden={props['aria-hidden']}
        aria-label={props['aria-label']}
      />
    );
  }

  return (
    <IconComponent data-component-id="src/components/ui/AppIcon.tsx:338:4" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="338" data-component-end-line="344" data-component-file="AppIcon.tsx" data-component-name="IconComponent" data-component-content="%7B%22elementName%22%3A%22IconComponent%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      width={size}
      height={size}
      className={`${disabled ? 'opacity-50 cursor-not-allowed' : onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
      onClick={disabled ? undefined : onClick}
      {...props}
    />
  );
}

export default Icon;
